import { useState, useEffect, useRef, useContext } from 'react'

import ctx from '@/context/AppContext'
import { getAccessKey } from '@/lib/accessKey'

interface GraphQLParams {
  query: string
  endpoint?: string
  variables?: any
  customHeaders?: any
}

export const graphQLQuery = async ({
  query,
  endpoint = `${import.meta.env.VITE_AGENT_GRAPHQL}`,
  variables = {},
  customHeaders = {},
}: GraphQLParams) => {
  const accessKey = getAccessKey()
  return await fetch(endpoint, {
    headers: {
      'Ocp-Apim-Subscription-Key': accessKey,
      'content-type': 'application/json',
      ...customHeaders,
    },
    method: 'POST',
    body: JSON.stringify({
      query: query,
      variables: { ...variables },
    }),
  })
}

export function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  })

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowSize
}

export const usePrevious = <T extends unknown>(value: T): T | undefined => {
  const ref = useRef<T>()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

export function usePartnerTheme(settings: any, setTheme: Function) {
  const { accessKey } = useContext(ctx)

  useEffect(() => {
    if (!settings?.workspaceSettings?.allowedCustomers || !accessKey) {
      return
    }
    const cusId = settings?.workspaceSettings?.allowedCustomers[0].id
    if (!cusId) {
      return
    }
    const requestBody = {
      query: `query{
        getThemeing(cId: "${cusId}"){
          theme
        }
      }`,
    }
    fetch(`${import.meta.env.VITE_AGENT_GRAPHQL}`, {
      headers: {
        'Ocp-Apim-Subscription-Key': accessKey,
      },
      method: 'POST',
      body: JSON.stringify(requestBody),
    })
      .then(res => {
        return res.json()
      })
      .then(resData => {
        if (resData.data.getThemeing.theme) {
          setTheme(JSON.parse(resData.data.getThemeing.theme))
        }
      })
      .catch(err => {
        console.log(err)
      })
  }, [settings, accessKey]) // eslint-disable-line react-hooks/exhaustive-deps
}

export const usePath = () => {
  const [path, setPath] = useState(window.location.pathname)
  const listenToPopstate = () => {
    const winPath = window.location.pathname
    setPath(winPath)
  }
  useEffect(() => {
    window.addEventListener('popstate', listenToPopstate)
    return () => {
      window.removeEventListener('popstate', listenToPopstate)
    }
  })
  return path
}

// TODO: replace with regex or something more substancial
export const useValidEmail = (email: string) => {
  email = email.trim()
  if (email.includes('@') && email.includes('.') && email.split('').length > 5) {
    return true
  } else {
    return false
  }
}
